/**
 * ================================================================================================
 *  THE ASSISTANT YOU CAN ASK A QUESTION. **Section 6, 2026-10-26.**
 * ================================================================================================
 *
 * The Owner, after driving the panel I shipped:
 *
 *   > *"בבדיקה העוזר נפתח, אך במסך שנבדק הוא מציג ״אין לי ערך מבוסס למסך הזה״. לכן הדרישה עדיין
 *   > אינה שלמה."*
 *   > *"אפשרות להקליד שאלה, למשל: ״איך מוסיפים משתמש ונותנים לו גישה לחברה?״ תשובה עם צעדים
 *   > מעשיים והפניה למסך המתאים."*
 *
 * and the route he approved for getting there:
 *
 *   > *"חיפוש במדריך ללא מודל שפה יכול להיות פתרון, אם הוא מאפשר שאלות ומחזיר תשובות שימושיות."*
 *
 * So: a typed question, matched against a written table, answered with ordered steps and a link to
 * the screen that does the job. No model, nothing generated, and **no customer text leaves this
 * estate** — the matching runs in the browser, which is the property that made this the route he
 * chose over a provider.
 *
 * ── WHY THE PREVIOUS PANEL ANSWERED NOTHING ON HIS SCREEN ───────────────────────────────────────
 *
 * It was keyed on `screenId` alone and held four screens. He opened a fifth and got the honest
 * no-answer state — which was correct, and useless. A person with a question does not know which
 * screen the answer lives on; that is the thing they are asking. Matching on the QUESTION is what
 * turns the same table into something that answers.
 *
 * ── THE MATCH IS DELIBERATELY DUMB, AND SAYS SO WHEN IT IS UNSURE ───────────────────────────────
 *
 * Shared-word scoring over a Hebrew-aware tokeniser. It cannot infer, it cannot paraphrase, and it
 * will not pretend: below the threshold it returns NOTHING, and the panel says it has no grounded
 * answer rather than offering the least-bad row. A help surface that always answers is a help
 * surface whose answers cannot be trusted — and `J_CHATBOT_DIAGNOSTIC_CONTRACT.md` J.0 rule 3 is
 * exactly this: say what you could not obtain, and stop.
 */

import { organisationWording } from '../api/organisationWording';
import { routes } from '../routes';
import type { Bilingual } from './guide';

/**
 * One thing a person can want to do, with the steps that do it.
 *
 * `asks` is not documentation — it is the match surface, and it carries the words somebody would
 * actually type, including the ones the product does not use for itself. Somebody searching for
 * "סיסמה" must find the invitation answer even though the screen calls it a credential.
 */
export interface HowTo {
  /** Stable, ASCII, never translated. Used as a key and in the panel's own tests. */
  readonly id: string;
  readonly title: Bilingual;
  /** Phrasings a person might type. Matched against, never displayed. */
  readonly asks: Bilingual;
  /** In order. Each step is something to do, not something to know. */
  readonly steps: readonly Bilingual[];
  /** Where the job is done. Omitted when the answer is a distinction rather than a task. */
  readonly href?: string;
  /**
   * Hidden from a session that does not hold it. Telling somebody how to do what the gate will
   * refuse costs them an afternoon and teaches them the product is broken.
   */
  readonly needs?: string;
}

/**
 * ── THE TABLE ───────────────────────────────────────────────────────────────────────────────────
 *
 * Covers the screens the Owner named: onboarding, users, roles, companies, permissions and
 * connections. Every step below describes a control that EXISTS in this build. Where something is
 * not built, the row says so in the step rather than being left out — a missing answer reads as a
 * gap in the assistant, while a stated limit reads as a fact about the product, and only one of
 * those is true.
 */
export const HOW_TO: readonly HowTo[] = [
  {
    id: 'add-user-and-grant-company',
    title: {
      he: 'להוסיף משתמש ולתת לו גישה לחברה',
      en: 'Add a user and give them access to a company',
    },
    asks: {
      he: 'איך מוסיפים משתמש חדש להזמין משתמש לתת גישה לחברה הרשאה למשתמש עובד חדש צירוף משתמש',
      en: 'how do I add a new user invite a user give access to a company permission for a user new employee',
    },
    steps: [
      { he: 'בהגדרות, פתח את "משתמשים והרשאות" ואז "משתמשים".', en: 'In Settings, open "People and permissions", then "Users".' },
      { he: 'בחר "הזמנת משתמש" והזן את הדואר האלקטרוני שלו.', en: 'Choose "Invite user" and enter their e-mail address.' },
      { he: 'בחר לו תפקיד. התפקיד קובע מה מותר לו לעשות — ואינו נותן גישה לאף חברה.', en: 'Pick a role. The role decides what they may do — and grants access to no company.' },
      { he: 'פתח "הרשאות לחברות" ובחר את המשתמש, את החברה ואת רמת הגישה.', en: 'Open "Company permissions" and choose the user, the company and the access level.' },
      { he: 'ההזמנה פעילה עד שהמשתמש משלים אותה. עד אז הוא מופיע ברשימה כ"הוזמן".', en: 'The invitation stands until they complete it. Until then they are listed as "invited".' },
    ],
    href: routes.tenantUsers,
    needs: 'tenant.user.invite',
  },
  {
    id: 'role-versus-company-access',
    title: {
      he: 'ההבדל בין תפקיד לבין גישה לחברה',
      en: 'The difference between a role and company access',
    },
    asks: {
      he: 'למה אני לא רואה חברה אין לי גישה תפקיד לא מספיק מנהל ולא רואה הבדל בין תפקיד להרשאה',
      en: 'why can I not see a company no access role is not enough admin but cannot see difference between role and permission',
    },
    steps: [
      { he: 'תפקיד קובע מה מותר לך לעשות. הוא אינו מקנה גישה לאף חברה.', en: 'A role decides what you may do. It grants access to no company.' },
      { he: 'גישה לחברה היא הענקה נפרדת, לכל חברה בנפרד — גם לבעלים של הארגון.', en: 'Company access is a separate grant, per company — including for the owner of the organisation.' },
      { he: 'לכן משתמש עם תפקיד ניהולי ובלי הענקה לא יראה אף חברה, וזה מצב תקין ולא תקלה.', en: 'So an administrator with no grant sees no company. That is correct behaviour, not a fault.' },
      { he: 'ההענקה נעשית ב"הרשאות לחברות", ואפשר לשנות רמה קיימת בלי להסיר ולהעניק מחדש.', en: 'Granting is done in "Company permissions", and an existing level can be changed without removing and re-granting.' },
    ],
    href: routes.tenantCompanyAccess,
  },
  {
    id: 'create-company',
    title: { he: 'להקים חברה חדשה', en: 'Create a new company' },
    asks: {
      he: 'איך מקימים חברה חדשה להוסיף חברה פתיחת חברה ח.פ מספר רשום הקמה',
      en: 'how do I create a new company add a company open a company registration number setup',
    },
    steps: [
      { he: 'בהגדרות, פתח "חברות ונתונים" ואז "חברות".', en: 'In Settings, open "Companies and data", then "Companies".' },
      { he: 'בחר "הקמת חברה" והזן את שם החברה ואת המספר הרשמי שלה.', en: 'Choose "Create company" and enter its name and official number.' },
      { he: 'לאחר ההקמה, הענק לעצמך גישה לחברה — הקמה אינה מעניקה גישה אוטומטית.', en: 'After creating it, grant yourself access — creating a company does not grant access.' },
      { he: 'פתח את כרטיס החברה והשלם את פרטיה בטאבים שבראש הכרטיס.', en: 'Open the company card and complete its details in the tabs at the top of the card.' },
    ],
    href: routes.tenantCompanies,
    needs: 'companies.company.create',
  },
  {
    id: 'change-organisation-name',
    title: { he: 'לשנות את שם הארגון', en: 'Change the organisation name' },
    asks: {
      he: 'איך משנים את שם הארגון שם התצוגה לוגו מיתוג השם לא מתעדכן',
      en: 'how do I change the organisation name display name logo branding the name does not update',
    },
    steps: [
      { he: 'בהגדרות, הטאב הראשון הוא "פרטי הארגון".', en: 'In Settings, the first tab is "Organisation".' },
      { he: 'הקלד את השם ולחץ "שמירת השם".', en: 'Type the name and press "Save name".' },
      { he: 'הכותרת ועמוד הבית מתעדכנים מיד. אין צורך לצאת ולהיכנס מחדש.', en: 'The header and the home page update immediately. No sign-out is needed.' },
      { he: 'השם הזה אינו קובע מי רשאי לראות אילו חברות, ואינו נגזר משם המשתמש המחובר.', en: 'This name decides nothing about access, and is never derived from the signed-in user.' },
    ],
    href: routes.tenantAdmin,
    needs: 'tenant.branding.manage',
  },
  {
    id: 'build-a-role',
    title: { he: 'לבנות תפקיד חדש או לערוך קיים', en: 'Build a new role or edit an existing one' },
    asks: {
      he: 'איך בונים תפקיד חדש לערוך תפקיד להרכיב הרשאות תפקיד מותאם הגדרת תפקידים',
      en: 'how do I build a new role edit a role compose permissions custom role role authoring',
    },
    steps: [
      { he: 'בהגדרות, "משתמשים והרשאות" ואז "הגדרת תפקידים".', en: 'In Settings, "People and permissions", then "Role authoring".' },
      { he: 'זה מסך אחר מ"תפקידים והרשאות": שם משייכים תפקיד למשתמש, כאן מרכיבים את התפקיד עצמו.', en: 'This is a different screen from "Roles and permissions": there you assign a role to a person, here you compose the role itself.' },
      { he: 'בחר את ההרשאות שהתפקיד יתיר. אפשר להעניק רק הרשאות שכבר יש לך.', en: 'Choose the permissions the role will allow. You can only grant permissions you already hold.' },
      { he: 'תפקיד אינו מקנה גישה לאף חברה, גם אם הוא כולל את כל ההרשאות.', en: 'A role grants access to no company, even if it holds every permission.' },
    ],
    href: routes.tenantRoleAuthoring,
    needs: 'tenant.role.manage',
  },
  {
    id: 'connect-accounting-system',
    title: { he: 'לחבר מערכת הנהלת חשבונות', en: 'Connect an accounting system' },
    asks: {
      he: 'איך מחברים הנהלת חשבונות חיבור למערכת חיצונית סנכרון נתונים פריוריטי חשבשבת ריווחית מקור נתונים',
      en: 'how do I connect accounting software external system data sync source connection',
    },
    steps: [
      { he: 'בהגדרות, "חברות ונתונים" ואז "מקורות נתונים".', en: 'In Settings, "Companies and data", then "Data sources".' },
      { he: 'בחר את המערכת. שם החיבור נוצר אוטומטית לפיה וניתן לשינוי.', en: 'Pick the system. The connection name is filled in from it and can be changed.' },
      { he: 'פתח את החיבור והשלם את פרטי ההתחברות. סיסמאות ומפתחות אינם נשמרים במערכת — רק הפניה אליהם.', en: 'Open the connection and complete its details. Passwords and keys are never stored here — only a reference to them.' },
      { he: 'קשר כל חברה במערכת המקור לחברה המתאימה כאן.', en: 'Link each company in the source system to the matching company here.' },
      { he: 'משיכת נתונים בפועל דורשת תוכנת חיבור למערכת הספציפית, והיא אינה כלולה בגרסה הזאת.', en: 'Actually pulling data needs connector software for that specific system, which this build does not carry.' },
    ],
    href: routes.tenantSources,
    needs: 'tenant.settings.manage',
  },
  {
    id: 'invitation-not-received',
    title: { he: 'המשתמש לא קיבל את ההזמנה', en: 'The user did not receive the invitation' },
    asks: {
      he: 'לא קיבל הזמנה הדואר לא הגיע מייל לא נשלח לשלוח שוב הזמנה חוזרת סיסמה ראשונה',
      en: 'did not receive invitation e-mail did not arrive resend invitation first password',
    },
    steps: [
      { he: 'פתח "משתמשים" ומצא אותו ברשימה. הזמנה קיימת מופיעה שם גם אם הדואר לא הגיע.', en: 'Open "Users" and find them in the list. An existing invitation is listed even when the mail did not arrive.' },
      { he: 'בחר "שליחה חוזרת". אין ליצור למשתמש חשבון שני — זה ייצור כפילות.', en: 'Choose "Resend". Do not create a second account for them — that produces a duplicate.' },
      { he: 'אם גם השליחה החוזרת אינה מגיעה, העתק את קוד הפנייה לתמיכה שבתחתית החלונית ומסור אותו.', en: 'If the resend does not arrive either, copy the support reference code at the foot of this panel and pass it on.' },
    ],
    href: routes.tenantUsers,
    needs: 'tenant.user.invite',
  },
  {
    id: 'two-factor',
    title: { he: 'אימות דו-שלבי', en: 'Two-factor authentication' },
    asks: {
      he: 'אימות דו שלבי קוד אימות אפליקציה טלפון אבטחת החשבון שני גורמים MFA',
      en: 'two factor authentication verification code authenticator app phone account security MFA',
    },
    steps: [
      { he: 'פתח "אבטחה" ואז "אבטחת החשבון".', en: 'Open "Security", then "Account security".' },
      { he: 'סרוק את הקוד באפליקציית האימות שבטלפון והזן את הקוד שהיא מציגה.', en: 'Scan the code with the authenticator app on your phone and enter the code it shows.' },
      { he: 'למנהלי ארגון האימות הוא חובה, ולכן הוא יידרש בכניסה הבאה.', en: 'For organisation administrators it is mandatory, so it will be required at the next sign-in.' },
    ],
    href: routes.personalSecurity,
  },
  {
    id: 'subscription-and-quota',
    title: { he: 'מנוי, מכסות וחשבוניות', en: 'Subscription, quotas and invoices' },
    asks: {
      he: 'מנוי מכסה כמה חברות כמה משתמשים חשבונית תשלום רישיון כמה זה עולה',
      en: 'subscription quota how many companies how many users invoice payment licence cost',
    },
    steps: [
      { he: 'בהגדרות, הטאב "מנוי וחשבוניות".', en: 'In Settings, the "Subscription and invoices" tab.' },
      { he: 'שם מוצגים התכנית, המודולים, ומכסת החברות והמשתמשים מול מה שבשימוש.', en: 'It shows the plan, the modules, and the company and user quotas against what is in use.' },
      { he: 'חריגה ממכסה אינה מוחקת דבר — היא חוסמת הוספה עד שהמכסה גדלה.', en: 'Exceeding a quota deletes nothing — it blocks adding until the quota is raised.' },
    ],
    href: routes.tenantSubscription,
  },
];

// ══════════════════════════════════════════════════════════════════════════════════════════════════
//  MATCHING
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Hebrew is written with the little words glued on. `לחברה`, `בחברה`, `מהחברה` and `החברה` are the
 * same word to a reader and four different strings to a computer, so one prefix letter is shaved
 * off tokens long enough to survive it. It is not morphology and does not pretend to be — it is
 * the smallest thing that stops `איך נותנים גישה לחברה` missing a row that says `חברה`.
 */
const PREFIXES = ['ה', 'ו', 'ב', 'ל', 'מ', 'ש', 'כ'];

function normalise(token: string): string {
  const bare = token.toLowerCase().replace(/[^֐-׿a-z0-9]/gu, '');
  if (bare.length > 3 && PREFIXES.includes(bare[0] ?? '')) return bare.slice(1);
  return bare;
}

/** Words too common to carry meaning; counting them would make every row match every question. */
const NOISE = new Set([
  'איך', 'מה', 'למה', 'איפה', 'אני', 'שלי', 'את', 'של', 'עם', 'לא', 'יש', 'אין', 'זה', 'כדי',
  'how', 'what', 'why', 'where', 'the', 'and', 'for', 'with', 'can', 'does', 'this', 'that', 'you',
]);

function tokens(text: string): string[] {
  /*
   * THE RETIRED NOUN IS NORMALISED OUT OF THE QUESTION RATHER THAN KEPT IN THE TABLE.
   *
   * People will go on typing the word the product used until last week -- a help surface that
   * cannot find an answer because the reader used yesterday's vocabulary is not a help surface.
   * The first attempt put the old word into this row's `asks` as a search term, and
   * `organisationWording.test.ts` refused it: the sweep forbids that noun anywhere in browser
   * source outside a comment, and that rule is worth more than the convenience.
   *
   * Normalising the INPUT gets both: the table speaks the product's language, and a question in
   * the old one still lands on the right row.
   */
  return organisationWording(text)
    .split(/\s+/u)
    .map(normalise)
    .filter((token) => token.length > 1 && !NOISE.has(token));
}

/** Everything about one answer that a question could legitimately match. */
function surfaceOf(entry: HowTo): Set<string> {
  const all = [
    entry.asks.he,
    entry.asks.en,
    entry.title.he,
    entry.title.en,
    ...entry.steps.map((step) => step.he),
    ...entry.steps.map((step) => step.en),
  ].join(' ');
  return new Set(tokens(all));
}

export interface HowToMatch {
  readonly entry: HowTo;
  /** Shared meaningful words. Exposed so the panel can order, and the tests can assert on it. */
  readonly score: number;
}

/**
 * The rows that answer this question, best first — **or nothing at all**.
 *
 * `capabilities` filters first: an answer the session could not act on is not a better answer than
 * silence. Two or fewer are returned, because a help panel that lists six possibilities has handed
 * the problem back.
 */
export function searchHowTo(
  question: string,
  capabilities: readonly string[],
): readonly HowToMatch[] {
  return searchHowToResult(question, capabilities).shown;
}

export interface HowToResult {
  readonly shown: readonly HowToMatch[];
  /**
   * HOW MANY ANSWERS WERE HELD BACK BECAUSE THE SESSION COULD NOT ACT ON THEM.
   *
   * Driven in a browser, the panel did something I had not intended and would not defend: asked
   * *"how do I add a user and give them access to a company"* by a session without the invite
   * capability, it hid that answer -- correctly, J.3 -- and SILENTLY SHOWED A DIFFERENT ONE. The
   * reader cannot tell a substitution from an answer, so the panel now says that something was
   * withheld and who can do it. Hiding an action is honest; replacing it without saying so is not.
   */
  readonly withheld: number;
}

export function searchHowToResult(
  question: string,
  capabilities: readonly string[],
): HowToResult {
  const asked = new Set(tokens(question));
  if (asked.size === 0) return { shown: [], withheld: 0 };

  const permitted = (entry: HowTo): boolean =>
    entry.needs === undefined || capabilities.includes(entry.needs);

  const scoreOf = (entry: HowTo): number => {
    const surface = surfaceOf(entry);
    let score = 0;
    for (const word of asked) if (surface.has(word)) score += 1;
    return score;
  };

  const floor = asked.size <= 1 ? 1 : 2;
  const withheld = HOW_TO.filter((entry) => !permitted(entry) && scoreOf(entry) >= floor).length;

  const scored = HOW_TO.filter(permitted)
    .map((entry) => {
      return { entry, score: scoreOf(entry) };
    })
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score);

  /*
   * THE THRESHOLD, AND IT IS THE HONEST PART OF THIS FILE.
   *
   * One shared word is a coincidence -- "חברה" appears in most of these rows -- so a single hit
   * only counts when the question was itself a single word. Anything vaguer returns NOTHING and
   * the panel says it has no grounded answer, which is J.0 rule 3 and is the whole reason this is
   * allowed to answer at all.
   */
  return { shown: scored.filter((match) => match.score >= floor).slice(0, 2), withheld };
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
//  CATEGORIES
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * FOUR WAYS IN, AND NOTHING ELSE ON ARRIVAL. **Owner, 2026-10-26.**
 *
 *   > *"למה הבוט שלנו מלא מידע לא קשור במקום לחלק לקטגוריות כמו שצריך? לתת את ה־3-4 הקטגוריות
 *   > החשובות וזהו, ומשם ילחצו וימשיכו."*
 *
 * He is right, and the panel he was looking at is the evidence: it opened with a paragraph about
 * where its answers come from, then a question box, then what the screen is, then a full entry with
 * its class and cause, then two paragraphs about what it cannot see. Everything true, all of it at
 * once, and none of it asked for.
 *
 * A category is ONE LINE until it is opened. So the panel arrives at the height of a search box and
 * four rows, and every further word is something the reader asked to see.
 *
 * ── THESE FOUR, AND WHY ─────────────────────────────────────────────────────────────────────────
 *
 * They are the four the settings screen already uses, which is not a coincidence and is the point:
 * somebody looking for help about permissions should find it under the same word they clicked to
 * get there. A help menu with its own private taxonomy is a second thing to learn.
 */
export interface HowToCategory {
  readonly id: string;
  readonly title: Bilingual;
  /** In display order. Every id must exist in `HOW_TO`; `howTo.search.test.ts` holds that. */
  readonly entries: readonly string[];
}

export const CATEGORIES: readonly HowToCategory[] = [
  {
    id: 'people',
    title: { he: 'משתמשים והרשאות', en: 'People and permissions' },
    entries: [
      'add-user-and-grant-company',
      'role-versus-company-access',
      'build-a-role',
      'invitation-not-received',
    ],
  },
  {
    id: 'companies',
    title: { he: 'חברות ונתונים', en: 'Companies and data' },
    entries: ['create-company', 'connect-accounting-system'],
  },
  {
    id: 'billing',
    title: { he: 'מנוי וחשבוניות', en: 'Subscription and invoices' },
    entries: ['subscription-and-quota'],
  },
  {
    id: 'security',
    title: { he: 'אבטחה וארגון', en: 'Security and organisation' },
    entries: ['two-factor', 'change-organisation-name'],
  },
];

/** One category's answers, filtered to what this session may actually act on (J.3). */
export function entriesIn(
  category: HowToCategory,
  capabilities: readonly string[],
): readonly HowTo[] {
  return category.entries
    .map((id) => HOW_TO.find((entry) => entry.id === id))
    .filter((entry): entry is HowTo => entry !== undefined)
    .filter((entry) => entry.needs === undefined || capabilities.includes(entry.needs));
}
